// ManageEvents.js
import React, { useState, useEffect } from 'react';
import { 
  getAllEvents, 
  createEvent, 
  updateEvent, 
  deleteEvent, 
  toggleEventParticipation 
} from '../../api/events';
import Button from '../../components/ui/Button';
import './ManageEvents.css';

const ManageEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    location: '',
    category: '',
    points: 0,
    participationEnabled: true,
  });
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const eventsData = await getAllEvents();
      setEvents(eventsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }

      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // Validation
      if (!formData.title || !formData.description || !formData.date) {
        setError('Please fill in all required fields');
        setSubmitting(false);
        return;
      }

      if (editingEvent) {
        // Update existing event
        await updateEvent(editingEvent.id, formData, imageFile);
        setSuccess('Event updated successfully!');
      } else {
        // Create new event
        if (!imageFile) {
          setError('Please upload an event image');
          setSubmitting(false);
          return;
        }
        await createEvent(formData, imageFile);
        setSuccess('Event created successfully!');
      }

      // Reset form and refresh events
      resetForm();
      await fetchEvents();
      setShowForm(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error submitting event:', err);
      setError(err.message || 'Failed to save event. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title || '',
      description: event.description || '',
      date: event.date || '',
      location: event.location || '',
      category: event.category || '',
      points: event.points || 0,
      participationEnabled: event.participationEnabled !== undefined ? event.participationEnabled : true,
    });
    setImagePreview(event.imageUrl || null);
    setImageFile(null);
    setShowForm(true);
    setError(null);
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteEvent(eventId);
      setSuccess('Event deleted successfully!');
      await fetchEvents();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting event:', err);
      setError('Failed to delete event. Please try again.');
    }
  };

  const handleToggleParticipation = async (eventId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      await toggleEventParticipation(eventId, newStatus);
      setSuccess(`Participation ${newStatus ? 'enabled' : 'disabled'} successfully!`);
      await fetchEvents();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error toggling participation:', err);
      setError('Failed to toggle participation. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      date: '',
      location: '',
      category: '',
      points: 0,
      participationEnabled: true,
    });
    setImageFile(null);
    setImagePreview(null);
    setEditingEvent(null);
    setError(null);
  };

  const handleCancelForm = () => {
    resetForm();
    setShowForm(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="manage-events">
      <div className="manage-events-header">
        <h1>Manage Events</h1>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} variant="primary">
            + Create New Event
          </Button>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {showForm && (
        <div className="event-form-container">
          <div className="event-form-header">
            <h2>{editingEvent ? 'Edit Event' : 'Create New Event'}</h2>
            <button className="close-btn" onClick={handleCancelForm}>×</button>
          </div>

          <form onSubmit={handleSubmit} className="event-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="title">Event Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter event title"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="category">Category</label>
                <input
                  type="text"
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  placeholder="e.g., Workshop, Conference, Seminar"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter event description"
                rows="5"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="date">Event Date *</label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="location">Location</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="Enter event location"
                />
              </div>

              <div className="form-group">
                <label htmlFor="points">Points</label>
                <input
                  type="number"
                  id="points"
                  name="points"
                  value={formData.points}
                  onChange={handleInputChange}
                  min="0"
                  placeholder="0"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="image">Event Image * {editingEvent && '(Leave empty to keep current image)'}</label>
              <input
                type="file"
                id="image"
                accept="image/*"
                onChange={handleImageChange}
              />
              <small className="form-help">Max size: 5MB. Formats: JPEG, PNG, GIF, WebP</small>
            </div>

            {imagePreview && (
              <div className="image-preview">
                <img src={imagePreview} alt="Preview" />
              </div>
            )}

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="participationEnabled"
                  checked={formData.participationEnabled}
                  onChange={handleInputChange}
                />
                <span>Enable user participation</span>
              </label>
            </div>

            <div className="form-actions">
              <Button type="button" onClick={handleCancelForm} variant="secondary">
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={submitting}>
                {submitting ? 'Saving...' : (editingEvent ? 'Update Event' : 'Create Event')}
              </Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading events...</div>
      ) : (
        <div className="events-table-container">
          <table className="events-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Title</th>
                <th>Date</th>
                <th>Category</th>
                <th>Participants</th>
                <th>Participation</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">No events found. Create your first event!</td>
                </tr>
              ) : (
                events.map((event) => (
                  <tr key={event.id}>
                    <td>
                      {event.imageUrl ? (
                        <img 
                          src={event.imageUrl} 
                          alt={event.title} 
                          className="event-thumbnail"
                        />
                      ) : (
                        <div className="no-image">No Image</div>
                      )}
                    </td>
                    <td>
                      <div className="event-title-cell">
                        <strong>{event.title}</strong>
                        {event.description && (
                          <small>{event.description.substring(0, 60)}...</small>
                        )}
                      </div>
                    </td>
                    <td>{formatDate(event.date)}</td>
                    <td>{event.category || 'N/A'}</td>
                    <td className="text-center">{event.participants?.length || 0}</td>
                    <td>
                      <button
                        className={`participation-toggle ${event.participationEnabled !== false ? 'enabled' : 'disabled'}`}
                        onClick={() => handleToggleParticipation(event.id, event.participationEnabled !== false)}
                        title={event.participationEnabled !== false ? 'Click to disable' : 'Click to enable'}
                      >
                        {event.participationEnabled !== false ? '✓ Enabled' : '✗ Disabled'}
                      </button>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Button 
                          onClick={() => handleEdit(event)} 
                          variant="secondary"
                          size="small"
                        >
                          Edit
                        </Button>
                        <Button 
                          onClick={() => handleDelete(event.id)} 
                          variant="danger"
                          size="small"
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ManageEvents;
